import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-alert-banner.css.lit';

import '../../base/outline-container/outline-container';
import { OutlineElement } from '../../base/outline-element/outline-element';
import '../ghs-icon/ghs-icon';
import { LightController } from '../../controllers/light-controller';

export type AlertVariant = 'information' | 'warning' | 'error' | 'success';
/**
 * The Alert Banner component
 *
 * @element ghs-alert-banner
 * @slot alert-banner-heading - The heading slot
 * @slot alert-banner-body-text - The body slot
 * @slot alert-banner-dismiss-button - The dismiss/close button slot
 */
@customElement('ghs-alert-banner')
export class GhsAlertBanner extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  lightController = new LightController(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * The alert type variant.
   * @param alertVariant
   * @default 'information'
   */
  @property({ type: String, attribute: 'alert-variant', reflect: true })
  alertVariant: AlertVariant = 'error';

  /**
   * Whether the alert is dismissible.
   * @param dismissible
   * @default 'dismissible'
   */
  @property({ type: Boolean, attribute: 'dismissible', reflect: true })
  dismissible = false;

  @state() hasHeading: boolean;
  @state() hasBodyText: boolean;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasHeading =
      this.querySelector('[slot="alert-banner-heading"]') !== null;
    this.hasBodyText =
      this.querySelector('[slot="alert-banner-body-text"]') !== null;
  }

  /**
   * Sets the icon name based on alertVariant's value.
   * @returns icon-name
   */
  setIcon() {
    switch (this.alertVariant) {
      case 'success':
        return 'check-circle';
      case 'warning':
        return 'info-triangle';
      default:
        return 'info-circle';
    }
  }

  render(): TemplateResult {
    return html`
      <div
        class="alert-banner ${this.alertVariant} ${this.dismissible
          ? 'dismissible'
          : ''}"
      >
        <outline-container site=${this.site}>
          <div class="alert-banner-inner">
            <div class="alert-banner-type-icon ${this.alertVariant}">
              <ghs-icon
                name="${this.setIcon()}"
                size=${this.site == 'BH' ? '28px' : '40px'}
                mobile-size="20px"
                site=${this.site}
              ></ghs-icon>
            </div>
            <div class="alert-banner-content-section">
              ${this.hasHeading
                ? html`
                    <div class="alert-banner-heading-subsection">
                      <div class="alert-banner-heading">
                        <slot name="alert-banner-heading"></slot>
                      </div>
                    </div>
                  `
                : nothing}
              ${this.hasBodyText
                ? html` <div class="alert-banner-body-text">
                    <slot name="alert-banner-body-text"></slot>
                  </div>`
                : nothing}
            </div>
          </div>

          ${this.dismissible
            ? html`
                <div class="alert-banner-dismiss-icon ${this.alertVariant}">
                  <slot name="alert-banner-dismiss-button"> </slot>
                </div>
              `
            : ''}
        </outline-container>
      </div>
    `;
  }
}
